import React, { createContext, useContext, useEffect, useState } from "react";
import {
  BSPType,
  DbBusinessWhatsappCustomHeader,
  ValidationError,
} from "./models";
import { whatsAppCustomHeaderService } from "./service";
import { useBusiness } from "../../business/provider";

interface WhatsappCustomHeaderProps {
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  customHeaderDetails: DbBusinessWhatsappCustomHeader;
  setCustomHeaderDetails: React.Dispatch<
    React.SetStateAction<DbBusinessWhatsappCustomHeader>
  >;
  showHeaderSetupModal: boolean;
  setShowHeaderSetupModal: React.Dispatch<React.SetStateAction<boolean>>;
  headerSetupCurrentStep: number;
  setHeaderSetupCurrentStep: React.Dispatch<React.SetStateAction<number>>;
  step1Errors: ValidationError[];
  setStep1Errors: React.Dispatch<React.SetStateAction<ValidationError[]>>;
  step2Errors: ValidationError[];
  error: string | null;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  setStep2Errors: React.Dispatch<React.SetStateAction<ValidationError[]>>;
  showPaymentProcessModal: boolean;
  setShowPaymentProcessModal: React.Dispatch<React.SetStateAction<boolean>>;
  showRazorpPayModal: boolean;
  setShowRazorpPayModal: React.Dispatch<React.SetStateAction<boolean>>;
  isProcessingPurchase: boolean;
  setIsProcessingPurchase: React.Dispatch<React.SetStateAction<boolean>>;
}

const WhatsappCustomHeaderContext =
  createContext<WhatsappCustomHeaderProps | null>(null);

const initialCustomHeaderDetails: DbBusinessWhatsappCustomHeader = {
  bsp_type: BSPType.Gupshup,
  app_id: null,
  app_name: "",
  waba_id: null,
  phone: "",
  country_code: "+91",
  display_name: "",
  contact_name: "",
  contact_phone: "",
  contact_country_code: "+91",
  contact_email: "",
  payment_id: null,
  embedded_signup_link: null,
  embedded_signup_status: null,
  is_registered: false,
  is_verified: false,
  created_at: null,
  updated_at: null,
  deleted_at: null,
};

export default function WhatsAppCustomHeaderProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [customHeaderDetails, setCustomHeaderDetails] =
    useState<DbBusinessWhatsappCustomHeader>(initialCustomHeaderDetails);
  const [showHeaderSetupModal, setShowHeaderSetupModal] =
    useState<boolean>(false);
  const [headerSetupCurrentStep, setHeaderSetupCurrentStep] =
    useState<number>(1);
  const [showPaymentProcessModal, setShowPaymentProcessModal] =
    useState<boolean>(false);
  const [showRazorpPayModal, setShowRazorpPayModal] = useState<boolean>(false);
  const [isProcessingPurchase, setIsProcessingPurchase] =
    useState<boolean>(false);
  const [step1Errors, setStep1Errors] = useState<ValidationError[]>([]);
  const [step2Errors, setStep2Errors] = useState<ValidationError[]>([]);
  const [error, setError] = useState<string | null>(null);
  const { selectedBusiness } = useBusiness();

  useEffect(() => {
    setHeaderSetupCurrentStep(1);
    setCustomHeaderDetails(initialCustomHeaderDetails);
    setIsLoading(true);
    setError(null);
    const fetchCustomHeader = async () => {
      if (selectedBusiness) {
        try {
          const customHeader =
            await whatsAppCustomHeaderService.getWhatsAppCustomHeader(
              selectedBusiness.id,
            );
          if (customHeader) setCustomHeaderDetails(customHeader);
        } catch (error: any) {
          setError(error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchCustomHeader();
  }, [selectedBusiness, setIsLoading, setCustomHeaderDetails, setError]);

  return (
    <WhatsappCustomHeaderContext.Provider
      value={{
        isLoading,
        setIsLoading,
        customHeaderDetails,
        setCustomHeaderDetails,
        showHeaderSetupModal,
        setShowHeaderSetupModal,
        headerSetupCurrentStep,
        setHeaderSetupCurrentStep,
        step1Errors,
        setStep1Errors,
        step2Errors,
        setStep2Errors,
        error,
        setError,
        showPaymentProcessModal,
        setShowPaymentProcessModal,
        showRazorpPayModal,
        setShowRazorpPayModal,
        isProcessingPurchase,
        setIsProcessingPurchase,
      }}
    >
      {children}
    </WhatsappCustomHeaderContext.Provider>
  );
}

export const useWhatsAppCustomHeader = () => {
  const context = useContext(WhatsappCustomHeaderContext);
  if (!context) {
    throw new Error(
      "useWhatsAppCustomHeader must be used within WhatsAppCustomHeaderProvider",
    );
  }
  return context;
};
